import React, { FC, ReactNode, useEffect, useMemo, useState, useCallback } from 'react';
import { Spin } from 'antd';
import { useRouter } from 'next/router';

import { useTranslation } from 'next-i18next';
import Switch from '../Switch';
import Pagination from './Pagination';
import styles from './PageList.module.scss';

interface DataItemType {
  [key: string]: any;
}

interface PageType {
  pageSize: number;
  pageNo: number;
  totalCount?: number;
}

export interface OnLoadDataResType {
  data: DataItemType[];
  page: PageType;
  status?: any;
}

export interface Props {
  defaultPageSize: number;
  prePageText?: string;
  nextPageText?: string;
  renderItem: (item: DataItemType) => ReactNode;
  onLoadData: (page: PageType) => Promise<OnLoadDataResType>;
  /** 筛选栏等额外内容，渲染在 Switch 下方、列表上方 */
  headerExtra?: ReactNode;
  /** headerExtra 下方、列表上方的额外内容（如汇总信息） */
  footerExtra?: ReactNode;
  /** Switch 同行右侧的额外内容（如筛选按钮） */
  switchExtra?: ReactNode;
  /**
   * Switch 的初始选中项（'0'=全部 '1'=已建成 '2'=建设中）。
   * 通过 key 强制重挂载时，可将此值设为当前选中项，避免 tab 被重置。
   */
  defaultSelectedIndex?: string;
  /** Switch 切换回调，可用于将选中状态提升到外部 */
  onSelectedIndexChange?: (value: string) => void;
  /**
   * 外部筛选条件的唯一标识，当它变化时只重置页码并重新请求，不销毁组件。
   * 与通过改变组件 key 重挂载不同，使用此 prop 可在新数据加载期间保留旧数据
   * 展示，避免列表区域闪烁抖动（与 Switch 切换的交互体验保持一致）。
   */
  filterKey?: string;
}

const PageList: FC<Props> = (props) => {
  const { t } = useTranslation('common');
  const {
    defaultPageSize,
    prePageText,
    nextPageText,
    onLoadData: onLoadDataProps,
    renderItem,
    headerExtra,
    footerExtra,
    switchExtra,
    defaultSelectedIndex,
    onSelectedIndexChange,
    filterKey,
  } = props;

  const playgroundDetailsConfig: {
    all: string;
    doing: string;
    end: string;
    emptyText: string;
    emptyDesc: string;
  } = t('playgroundDetailsConfig', { returnObjects: true });

  const [data, setData] = useState([] as DataItemType);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState({
    pageNo: 1,
    pageSize: defaultPageSize,
  });
  const [selectedIndex, setSelectedIndex] = useState(defaultSelectedIndex ?? '1');
  const { locale } = useRouter();

  // 切换页码
  const onPageSizeChange = (page: number, pageSize: number) => {
    setPages({ pageNo: page, pageSize });
  };
  // 请求函数
  const onLoadData = useCallback(
    async ({ pageNo, pageSize, status }: any) => {
      const params = { pageNo, pageSize, status };
      try {
        setLoading(true);
        const { data: resData, page: resPage } = await onLoadDataProps(params);
        const { totalCount } = resPage as { totalCount: number };
        setData(resData);
        setTotal(totalCount);
        // 切换完页面滚动到顶部 todo==增加滚动到容器视窗内
        document.documentElement.scrollTop = 0;
      } finally {
        setLoading(false);
      }
    },
    [onLoadDataProps],
  );

  const status = useMemo(() => {
    if (selectedIndex === '1') {
      return 'EXECUTED';
    }
    if (selectedIndex === '2') {
      return 'RESERVE';
    }
    return '';
  }, [selectedIndex]);

  const handleSelectTag = ({ value }: { value: string }) => {
    setSelectedIndex(value);
    onSelectedIndexChange?.(value);
    onPageSizeChange(1, defaultPageSize);
  };

  // 外部筛选条件变化时，重置页码到第 1 页（不销毁组件，保留旧数据直到新数据加载完毕）
  const prevFilterKeyRef = React.useRef<string | undefined>(filterKey);
  useEffect(() => {
    if (filterKey !== undefined && filterKey !== prevFilterKeyRef.current) {
      prevFilterKeyRef.current = filterKey;
      setPages({ pageNo: 1, pageSize: defaultPageSize });
    }
  }, [filterKey, defaultPageSize]);

  // defaultPageSize 变化（如屏幕尺寸切换 sm/lg）时同步更新 pages.pageSize
  const prevDefaultPageSizeRef = React.useRef(defaultPageSize);
  useEffect(() => {
    if (defaultPageSize !== prevDefaultPageSizeRef.current) {
      prevDefaultPageSizeRef.current = defaultPageSize;
      setPages({ pageNo: 1, pageSize: defaultPageSize });
    }
  }, [defaultPageSize]);

  // 所有请求的params准备
  const params = useMemo(() => {
    return {
      ...pages,
      status,
      lanType: locale,
    };
  }, [pages, locale, status]);

  const switchOptions = [
    { text: playgroundDetailsConfig.all, value: '0' },
    { text: playgroundDetailsConfig.end, value: '1' },
    { text: playgroundDetailsConfig.doing, value: '2' },
  ];

  useEffect(() => {
    // 初始化的时候执行一次
    onLoadData(params);
  }, [params, onLoadData]);

  const listItemNode =
    Array.isArray(data) &&
    data.map((item, idx) => {
      const RenderItemNode = renderItem(item);
      return (
        // eslint-disable-next-line react/no-array-index-key
        <div className={styles['list-item']} key={idx}>
          {RenderItemNode}
        </div>
      );
    });

  // 加载完成且数据为空时显示空态提示
  const isEmpty = !loading && Array.isArray(data) && data.length === 0;

  return (
    <div className={styles['page-list-container']}>
      <Spin spinning={loading}>
        <div className={styles['list-wrap']}>
          <div
            className={styles['switch-row']}
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'nowrap',
              gap: '12px',
            }}
          >
            <Switch
              className={styles['switch-container']}
              value={selectedIndex}
              onChange={handleSelectTag}
              options={switchOptions}
            />
            {switchExtra}
          </div>
          {headerExtra}
          {footerExtra}
          <div className={styles['list-container']}>
            {isEmpty ? (
              <div className={styles['empty-state']}>
                <div className={styles['empty-icon']}>
                  <svg viewBox="0 0 120 96" fill="none" xmlns="http://www.w3.org/2000/svg">
                    {/* 盒子底部阴影 */}
                    <ellipse cx="60" cy="82" rx="36" ry="6" fill="#f0f0f0" />
                    {/* 盒子主体 */}
                    <path
                      d="M28 38H92V74C92 76.2 90.2 78 88 78H32C29.8 78 28 76.2 28 74V38Z"
                      fill="#fafafa"
                      stroke="#d9d9d9"
                      strokeWidth="1.5"
                    />
                    {/* 盒子上沿翻盖 */}
                    <path
                      d="M24 30C24 28.3 25.3 27 27 27H57L60 33H93C94.7 33 96 34.3 96 36V38H28V36C28 34.3 26 32 24 30Z"
                      fill="#fafafa"
                      stroke="#d9d9d9"
                      strokeWidth="1.5"
                      strokeLinejoin="round"
                    />
                    {/* 盒子中间折线 */}
                    <path
                      d="M28 38L44 38L48 44H72L76 38L92 38"
                      stroke="#e8e8e8"
                      strokeWidth="1.5"
                      strokeLinejoin="round"
                    />
                    {/* 虚线暗示空内容 */}
                    <path
                      d="M48 56H72"
                      stroke="#d9d9d9"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeDasharray="4 3"
                    />
                    <path
                      d="M52 64H68"
                      stroke="#e8e8e8"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeDasharray="3 3"
                    />
                  </svg>
                </div>
                <div className={styles['empty-title']}>{playgroundDetailsConfig.emptyText}</div>
                <div className={styles['empty-desc']}>{playgroundDetailsConfig.emptyDesc}</div>
              </div>
            ) : (
              listItemNode
            )}
          </div>
        </div>
        {!!total && (
          <Pagination
            key={status}
            preText={prePageText}
            nextText={nextPageText}
            pageSize={defaultPageSize}
            total={total}
            onChange={onPageSizeChange}
          />
        )}
      </Spin>
    </div>
  );
};

export default PageList;
